// src/pages/CampusMap.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import BottomNav from '../components/BottomNav';
import MoreButton from '../components/MoreButton';
import AppDrawer from '../components/AppDrawer';
import DesktopNav from '../components/DesktopNav';
import { AlertBanner } from '../components/AlertBanner';
import './CampusMap.css';

const MAP_IMAGE = '/vu-connect/Campus_Map_2026.jpg';
const MAP_PDF = '/vu-connect/campus-map-2026.pdf';

const CampusMap = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleImageError = () => {
    setImageError(true);
    setIsLoading(false);
  };

  return (
    <div className="campus-map-page">
      <header className="dashboard-header">
        <div className="dashboard-brand">
          <button
            className="back-button"
            onClick={handleBack}
            aria-label="Go back to dashboard"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
          </button>
          <h1>Campus Map</h1>
        </div>
        <div className="dashboard-header-right">
          <a
            className="btn-refresh"
            href={MAP_PDF}
            download
            aria-label="Download campus map PDF"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </a>
          <MoreButton onClick={() => setDrawerOpen(true)} />
        </div>
      </header>
      <AlertBanner />
      <DesktopNav />
      <div className="map-container">
        {isLoading && (
          <div className="map-loading">
            <div className="loading-spinner"></div>
            <p>Loading campus map...</p>
          </div>
        )}

        {imageError && !isLoading && (
          <div className="map-error">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            <h2>Unable to load map</h2>
            <p>Please check that the campus map image is in the public folder</p>
            <button className="btn-retry" onClick={() => window.location.reload()}>
              Try Again
            </button>
          </div>
        )}

        {!imageError && (
          <TransformWrapper
            initialScale={1}
            minScale={1}
            maxScale={6}
            centerOnInit
            doubleClick={{ mode: 'zoomIn', step: 0.7 }}
            wheel={{ step: 0.08 }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                <TransformComponent
                  wrapperClass="map-viewport"
                  contentClass="map-image-content"
                >
                  <img
                    src={MAP_IMAGE}
                    alt="Viterbo University Campus Map"
                    className={`campus-map-image ${!isLoading ? 'loaded' : ''}`}
                    onLoad={() => setIsLoading(false)}
                    onError={handleImageError}
                    draggable={false}
                  />
                </TransformComponent>

                <div className="map-controls">
                  <button className="map-control-btn" onClick={() => zoomIn()} aria-label="Zoom in">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                      <line x1="11" y1="8" x2="11" y2="14"></line>
                      <line x1="8" y1="11" x2="14" y2="11"></line>
                    </svg>
                  </button>
                  <button className="map-control-btn" onClick={() => zoomOut()} aria-label="Zoom out">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                      <line x1="8" y1="11" x2="14" y2="11"></line>
                    </svg>
                  </button>
                  <button className="map-control-btn" onClick={() => resetTransform()} aria-label="Reset view">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                      <path d="M21 3v5h-5"></path>
                      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                    </svg>
                  </button>
                </div>
              </>
            )}
          </TransformWrapper>
        )}
      </div>
      <AppDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} onOpen={() => setDrawerOpen(true)} />
      <BottomNav />
    </div>
  );
};

export default CampusMap;
